import {
  FieldWrapper,
  FormField,
  FormGrid,
  InputSelectionList,
  SelectionList,
  SelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import { getItemTitle, ListItem } from 'lib-knowledge-form';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  LanguageId,
  OrganizationSettingsDocument,
  OrganizationSettingsQuery,
  useOrganizationSetDefaultLanguageMutation,
} from 'lib-knowledge-management-common/graphql';
import { getLabelsForLanguage } from 'lib-knowledge-management-common/utils/language';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useContext } from 'react';
import { StrictOmit } from 'ts-essentials';

type OnChangeItem = StrictOmit<
  SelectionListChangeEvent<{
    id: LanguageId;
    name: string;
  }>,
  'selectedItem'
>;

const placeholders = {
  loading: 'Loading available languages...',
  empty: 'Select default language',
};

export function DefaultLanguageForm(): JSX.Element {
  const {
    defaultLanguage,
    handleDefaultLanguageChange,
    isSettingDefaultLanguage,
    listItems,
  } = useDefaultLanguageForm();

  return (
    <FormGrid>
      <FormField span={3}>
        <FieldWrapper label="Default language" required={true}>
          <InputSelectionList
            multiple={false}
            value={[
              {
                id: (defaultLanguage?.languageId as LanguageId) ?? '',
                name: defaultLanguage?.description ?? '',
              },
            ]}
            onChange={handleDefaultLanguageChange}
            disabled={isSettingDefaultLanguage}
            placeholder={placeholders.empty}
            equalityFn={(a, b): boolean => a.id === b.id}
            getItemTitleByValue={getItemTitle}
            selectionListProps={{
              style: { maxHeight: 300, overflow: 'auto' },
            }}
          >
            {listItems.map((listItem) => (
              <SelectionList.Item key={listItem.id} value={listItem}>
                <Text.Paragraph>{listItem.name}</Text.Paragraph>
              </SelectionList.Item>
            ))}
          </InputSelectionList>
        </FieldWrapper>
      </FormField>
    </FormGrid>
  );
}

// TODO: move to it's own file
function useDefaultLanguageForm() {
  const { errorNotify, successNotify } = useNotification();
  const { defaultLanguage, enabledLanguages } = useContext(LanguageContext);

  const listItems: ListItem[] = enabledLanguages.map((lang) => ({
    id: lang.languageId,
    name: lang.description,
  }));

  const [setDefaultLang, { loading: isSettingDefaultLanguage }] =
    useOrganizationSetDefaultLanguageMutation();

  const executeSetDefaultLanguage = async (item: OnChangeItem) => {
    const newDefaultLangId = item.selectedValues[0].id;
    const newDefaultLang = getLabelsForLanguage({
      languageId: newDefaultLangId,
      languages: enabledLanguages,
    });

    try {
      await setDefaultLang({
        variables: {
          languageId: item.selectedValues[0].id,
        },
        update: (_cache) => {
          const currentOrgSettings =
            _cache.readQuery<OrganizationSettingsQuery>({
              query: OrganizationSettingsDocument,
            });
          if (!currentOrgSettings) return;

          _cache.writeQuery<OrganizationSettingsQuery>({
            data: {
              ...currentOrgSettings,
              organisationSettings: {
                ...currentOrgSettings.organisationSettings,
                defaultLanguage: {
                  __typename: 'Language',
                  description: newDefaultLang.description,
                  languageId: newDefaultLangId,
                  nativeDescription: newDefaultLang.nativeDescription,
                },
              },
            },
            query: OrganizationSettingsDocument,
          });
        },
      });
      successNotify({
        title: 'Success',
        text: `Default language set to "${item.selectedValues[0].name}"`,
      });
    } catch (_error) {
      errorNotify({
        text: 'Default language could not be set, please refresh and try again',
        time: 10000,
      });
    }
  };

  return {
    listItems,
    handleDefaultLanguageChange: executeSetDefaultLanguage,
    isSettingDefaultLanguage,
    defaultLanguage,
  };
}
